import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Copy, Eye, EyeOff, RefreshCw, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { School } from "@/hooks/useSchools";
import { supabase } from "@/integrations/supabase/client";

interface ViewLoginCredentialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  school: School | null;
  onCredentialsRegenerated?: () => void;
}

const ViewLoginCredentialsDialog = ({
  open,
  onOpenChange,
  school,
  onCredentialsRegenerated,
}: ViewLoginCredentialsDialogProps) => {
  const { toast } = useToast();
  const [showPassword, setShowPassword] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [newCredentials, setNewCredentials] = useState<{
    email: string;
    password: string;
    subscription_id: string;
  } | null>(null);

  if (!school) return null;

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "تم النسخ",
      description: `تم نسخ ${label} إلى الحافظة`,
    });
  };

  const handleRegenerateCredentials = async () => {
    if (!school) return;

    setIsRegenerating(true);
    try {
      // console.log(
      //   "Attempting to regenerate credentials for school:",
      //   school.id
      // );

      // استخدام supabase.functions.invoke بدلاً من fetch مباشرة
      const { data, error } = await supabase.functions.invoke(
        "regenerate-school-credentials",
        {
          body: { school_id: school.id },
        }
      );


      if (error) {
        console.error("Supabase function error:", error);
        toast({
          title: "حدث خطأ",
          description: error.message || "فشل في توليد بيانات دخول جديدة",
          variant: "destructive",
        });
        return;
      }

      if (data?.success) {
        setNewCredentials(data.credentials);
        toast({
          title: "تم بنجاح",
          description: "تم توليد بيانات دخول جديدة للمدرسة",
        });
        onCredentialsRegenerated?.();
      } else {
        toast({
          title: "حدث خطأ",
          description: data?.error || "فشل في توليد بيانات دخول جديدة",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error regenerating credentials:", error);
      toast({
        title: "حدث خطأ",
        description: "فشل في الاتصال بالخادم",
        variant: "destructive",
      });
    } finally {
      setIsRegenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md [&>button]:hidden" dir="rtl">
        <DialogHeader className="bg-orange-50 p-4 rounded-t-lg -mx-6 -mt-6 mb-4">
          <DialogTitle className="text-orange-600 text-right">
            توليد بيانات دخول جديدة
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {newCredentials ? (
            // عرض البيانات الجديدة المولدة
            <>
              {/* <div>
                <Label className="text-sm font-medium text-gray-700">
                  رقم الاشتراك
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={newCredentials.subscription_id}
                    readOnly
                    className="bg-gray-50"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      copyToClipboard(
                        newCredentials.subscription_id,
                        "رقم الاشتراك"
                      )
                    }
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div> */}

              {/* <div>
                <Label className="text-sm font-medium text-gray-700">
                  البريد الإلكتروني الجديد
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={newCredentials.email}
                    readOnly
                    className="bg-green-50 border-green-200"
                    dir="ltr"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      copyToClipboard(newCredentials.email, "البريد الإلكتروني")
                    }
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div> */}

              <div>
                <Label className="text-sm font-medium text-gray-700">
                  كلمة المرور الجديدة
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newCredentials.password}
                    readOnly
                    className="bg-green-50 border-green-200"
                    dir="ltr"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      copyToClipboard(newCredentials.password, "كلمة المرور")
                    }
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>

              <div className="bg-green-50 p-3 rounded-md">
                <p className="text-sm text-green-800">
                  <strong>تم بنجاح:</strong> تم توليد بيانات دخول جديدة للمدرسة.
                  احفظ هذه البيانات في مكان آمن.
                </p>
              </div>

              <div className="bg-amber-50 p-3 rounded-md">
                <p className="text-sm text-amber-800">
                  <strong>تنبيه:</strong> البيانات القديمة لم تعد صالحة. يجب
                  استخدام البيانات الجديدة لتسجيل الدخول.
                </p>
              </div>
            </>
          ) : (
            // عرض نموذج توليد بيانات جديدة
            <>
              <div className="bg-amber-50 p-4 rounded-md">
                <div className="flex items-center gap-2 mb-3">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  <strong className="text-amber-800">تحذير:</strong>
                </div>
                <p className="text-sm text-amber-800 mb-3">
                  سيؤدي توليد كلمة دخول جديدة لمدير المدرسة إلى إلغاء كلمة
                  المرور الحالية نهائياً.{" "}
                </p>
                <ul className="text-xs text-amber-700 list-disc list-inside space-y-1">
                  <li>سيتم توليد كلمة مرور جديدة</li>
                  <li>البيانات القديمة ستصبح غير صالحة</li>
                  <li>يجب إعطاء البيانات الجديدة للمدرسة</li>
                </ul>
              </div>

              <div className="bg-blue-50 p-3 rounded-md">
                <p className="text-sm text-blue-800">
                  <strong>ملاحظة:</strong> تأكد من حفظ البيانات الجديدة في مكان
                  آمن بعد التوليد.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-4">
          {!newCredentials && (
            <>
              <Button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  handleRegenerateCredentials();
                }}
                disabled={isRegenerating}
                className="bg-orange-600 hover:bg-orange-700 text-white min-w-[120px]"
              >
                {isRegenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    جاري التوليد...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    توليد
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  setNewCredentials(null);
                  setShowPassword(false);
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 min-w-[120px]"
              >
                إغلاق
              </Button>
            </>
          )}
          {newCredentials && (
            <>
              <Button
                onClick={() => {
                  setNewCredentials(null);
                  setShowPassword(false);
                }}
                variant="outline"
                className="min-w-[120px]"
              >
                توليد بيانات أخرى
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  onOpenChange(false);
                  setNewCredentials(null);
                  setShowPassword(false);
                }}
                className="bg-gray-100 hover:bg-gray-200 text-gray-800 min-w-[120px]"
              >
                إغلاق
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ViewLoginCredentialsDialog;
